/**
 * Bootstrap 5.3 Color Mode Utilities
 * Handles light/dark/auto theme switching and persistence
 */

export type ColorMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'bws-color-mode';

/**
 * Get the stored color mode preference or default to 'auto'
 */
export const getStoredColorMode = (): ColorMode => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'auto') {
    return stored;
  }
  return 'auto';
};

/**
 * Set and persist the color mode preference
 */
export const setColorMode = (mode: ColorMode): void => {
  localStorage.setItem(STORAGE_KEY, mode);
  applyColorMode(mode);
};

/**
 * Apply the color mode to the document
 */
const applyColorMode = (mode: ColorMode): void => {
  const root = document.documentElement;

  if (mode === 'auto') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.setAttribute('data-bs-theme', prefersDark ? 'dark' : 'light');
  } else {
    root.setAttribute('data-bs-theme', mode);
  }
};

/**
 * Get the active theme (resolves 'auto' to actual theme)
 */
export const getActiveTheme = (): 'light' | 'dark' => {
  const mode = getStoredColorMode();
  if (mode === 'auto') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
};

/**
 * Initialize color mode on app startup
 */
export const initializeColorMode = (): void => {
  const storedMode = getStoredColorMode();
  applyColorMode(storedMode);

  // Listen for system preference changes when in auto mode
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const currentMode = getStoredColorMode();
    if (currentMode === 'auto') {
      applyColorMode('auto');
    }
  });
};
